import type { BillType, DiscountType } from "@/data/model/Enums";
import type { IBaseOpenBill } from "@/data/model/Operator";



import type { IOrderItemComment } from "./Order";
import type { IMultiLanguageText } from "./Shared";


export interface IBill {
    id: string;
    tableId: string;
    opened: Date;
    closed?: Date;
    total: number;
    totalPaid: number;
    unpaidItems: Array<IBillItem>;
    paidItems: Array<IBillItem>;
    selections: Array<IUserItemSelection>;
    type: BillType;
    customName?: string;
    selectionDiscountAmount: number; // todo: to remove?
    selectionTotalAfterItemDiscounts: number;
    selectionTotalDiscountsAndVouchersAmount: number;
    selectionTotalAfterDiscount: number;
    manualEndUsersCount?: number;
    hasDiscount: boolean;
    selectionHasVoucher: boolean;
    selectedQuantityToAmountMap: Map<string, IBillItemSelectionAmounts[]>;
    appliedDiscounts: Array<IBillDiscount>;
    appliedItemDiscounts: Array<IBillItemDiscount>;
    appliedItemDiscountSummaries: Array<IBillItemDiscountSummary>;
    appliedBillDiscountSummaries: Array<IBillDiscountSummary>;
    totalUnpaidConsumption: number;
    totalUnpaidDiscountsAmount: number;
    totalUnpaidAmountAfterDiscounts: number;
}

export interface IBillItem {
    id: string;
    orderItemId: string;
    orderId: string;
    articleId: string | undefined;
    name?: IMultiLanguageText;
    internalName?: string;
    comment: IOrderItemComment | undefined;
    isPricePerMinute: boolean;
    timerStartedDateTime: Date | undefined;
    timerStoppedDateTime: Date | undefined;
    // the elapsed time of the timer as the server told it, and when the device read it
    timerElapsedSeconds: number;
    timerElapsedReadDateTime: Date;
    timerMinuteRate: number | undefined;
    timerDiscounts: IBillItemTimerDiscount[];
    quantity: number;
    paidQuantity: number;
    unpaidQuantity: number;
    paidAmount: number;
    unitPrice: number;
    unitPriceWithOptions: number;
    isZeroPriceItem: boolean;
    sortOrder: number;
    options: Array<IBillItemOptionGroup>;

    unitPriceAfterDiscount: number;
    unitPriceAfterDiscountWithOptions: number;

    lastDiscountType: DiscountType | undefined;
    hasDiscount: boolean;
    hasProcessedItemLevelDiscount: boolean;
}

// An active item discount of a running timer: the device works its amount out from the price it counts on, in the order the
// discounts were applied, like the server (BillItem.GetPricePerMinuteDiscountAmount)
export interface IBillItemTimerDiscount {
    discountType: DiscountType;
    percentage: number | undefined;
    absoluteAmount: number | undefined;
}

export interface IBillItemOptionGroup {
    id: string;
    displayName: boolean;
    name?: IMultiLanguageText;
    internalName?: string;
    items: Array<IBillItemOption>;
}

export interface IBillItemOption {
    id: string;
    articleId?: string;
    name?: IMultiLanguageText;
    internalName?: string;
    quantity: number;
    unitPrice: number;
    options: Array<IBillItemOptionGroup>;
    unitPriceAfterDiscount: number;
    lastDiscountType: DiscountType | undefined;
    hasDiscount: boolean;
    appliedDiscountSplits: Array<IBillDiscountItemSplit>;
    hasProcessedItemLevelDiscount: boolean;
}

export interface IUserItemSelection {
    identityId: string;
    quantity: number;
    billItemId: string;
}

export interface ISelectedItem {
    billItemId: string;
    quantity: number;
}

export interface IBillDiscount {
    id: string;
    discountId?: string;
    voucherId?: string;
    identityId?: string;
    createdDateTime: Date;
    amount: number;
    isProcessed: boolean;
    discountName?: IMultiLanguageText;
    voucherCode?: string;
}

export interface IBillItemDiscount {
    id: string;
    billItemId: string;
    discountId: string;
    discountType: DiscountType;
    createdDateTime: Date;
    unitDiscountAmount: number;
    isProcessed: boolean;
}

export interface IBillItemDiscountSummary {
    discountId: string;
    discountType: DiscountType;
    createdDateTime: Date;
    isProcessed: boolean;
    discountName: IMultiLanguageText;
    appliedAmount: number;
}

export interface IBillDiscountSummary {
    discountId: string;
    appliedAmount: number;
}

export interface IBillItemSelectionAmounts {
    selectedQuantity: number;
    totalAfterItemDiscounts: number;
    billDiscountsAndVouchersAmount: number;
    totalDiscountsAndVouchersAmount: number;
    totalAfterBillDiscountsAndVouchers: number;
    totalAfterBillDiscounts: number;
}

export interface IBillDiscountItemSplit {
    id: string;
    billDiscountId: string;
    billItemId: string;
    discountedQuantity: number;
    totalDiscountAmount: number;
    isVoucherSplit: boolean;
    isProcessed: boolean;
    createdDateTime: Date;
}

export class Bill {
    static getUnpaidLineItems(bill: IBill, identityId: string): UnpaidLineItem[] {
        const unpaidLineItems = bill.unpaidItems.map(
            (lineItem) => new UnpaidLineItem(lineItem, Bill.getSelectedItemQuantity(identityId, lineItem, bill))
        );
        unpaidLineItems.sort((a, b) => a.sortOrder - b.sortOrder);

        return unpaidLineItems;
    }

    static getPaidLineItems(bill: IBill): PaidLineItem[] {
        const paidLineItems = bill.paidItems.map((lineItem) => new PaidLineItem(lineItem));
        paidLineItems.sort((a, b) => a.sortOrder - b.sortOrder);

        return paidLineItems;
    }

    static getSelectedItemQuantity(identityId: string, item: IBillItem, bill: IBill): number {
        // get all user selections for bill
        const lineSelections = bill.selections.filter((x) => x.identityId === identityId && x.billItemId == item.id);

        // get all selected quantities for line item
        let quantity = 0;
        for (const selection of lineSelections) {
            quantity += selection.quantity;
        }
        return quantity;
    }

    static getUnpaidMaxDisplayedDigits(items: IBillItem[]) {
        let maxLength = 1;
        for (const item of items) {
            const itemDigits = item.unpaidQuantity.toString().length;
            maxLength = Math.max(maxLength, itemDigits);
        }
        return maxLength;
    }

    static getPaidMaxDisplayedDigits(items: IBillItem[]) {
        let maxLength = 1;
        for (const item of items) {
            const itemDigits = item.paidQuantity.toString().length;
            maxLength = Math.max(maxLength, itemDigits);
        }
        return maxLength;
    }
}

export class UnpaidLineItem implements IBillItem {
    id: string;
    orderId: string;
    orderItemId: string;
    articleId: string | undefined;
    name?: IMultiLanguageText;
    internalName?: string;
    comment: IOrderItemComment | undefined;
    isPricePerMinute: boolean;
    timerStartedDateTime: Date | undefined;
    timerStoppedDateTime: Date | undefined;
    // the elapsed time of the timer as the server told it, and when the device read it
    timerElapsedSeconds: number;
    timerElapsedReadDateTime: Date;
    timerMinuteRate: number | undefined;
    timerDiscounts: IBillItemTimerDiscount[];
    quantity: number;
    paidQuantity: number;
    unpaidQuantity: number;
    paidAmount: number;
    unitPrice: number;
    unitPriceWithOptions: number;
    isZeroPriceItem: boolean;
    sortOrder: number;
    options: Array<IBillItemOptionGroup>;
    selectedQuantity: number;
    unitPriceAfterDiscount: number;
    unitPriceAfterDiscountWithOptions: number;
    lastDiscountType: DiscountType | undefined;
    hasDiscount: boolean;
    hasProcessedItemLevelDiscount: boolean;

    constructor(lineItem: IBillItem, selectedQuantity: number) {
        this.id = lineItem.id;
        this.orderId = lineItem.orderId;
        this.orderItemId = lineItem.orderItemId;
        this.articleId = lineItem.articleId;
        this.name = lineItem.name;
        this.internalName = lineItem.internalName;
        this.comment = lineItem.comment;
        this.isPricePerMinute = lineItem.isPricePerMinute;
        this.timerStartedDateTime = lineItem.timerStartedDateTime;
        this.timerStoppedDateTime = lineItem.timerStoppedDateTime;
        this.timerElapsedSeconds = lineItem.timerElapsedSeconds;
        this.timerElapsedReadDateTime = lineItem.timerElapsedReadDateTime;
        this.timerMinuteRate = lineItem.timerMinuteRate;
        this.timerDiscounts = lineItem.timerDiscounts;
        this.quantity = lineItem.quantity;
        this.paidQuantity = lineItem.paidQuantity;
        this.unpaidQuantity = lineItem.unpaidQuantity;
        this.paidAmount = lineItem.paidAmount;
        this.unitPrice = lineItem.unitPrice;
        this.unitPriceWithOptions = lineItem.unitPriceWithOptions;
        this.isZeroPriceItem = lineItem.isZeroPriceItem;
        this.sortOrder = lineItem.sortOrder;
        this.options = lineItem.options;
        this.selectedQuantity = selectedQuantity;
        this.unitPriceAfterDiscount = lineItem.unitPriceAfterDiscount;
        this.unitPriceAfterDiscountWithOptions = lineItem.unitPriceAfterDiscountWithOptions;
        this.lastDiscountType = lineItem.lastDiscountType;
        this.hasDiscount = lineItem.hasDiscount;
        this.hasProcessedItemLevelDiscount = lineItem.hasProcessedItemLevelDiscount;
    }
}

export class PaidLineItem implements IBillItem {
    id: string;
    orderId: string;
    orderItemId: string;
    articleId: string | undefined;
    name?: IMultiLanguageText;
    internalName: string | undefined;
    comment: IOrderItemComment | undefined;
    isPricePerMinute: boolean;
    timerStartedDateTime: Date | undefined;
    timerStoppedDateTime: Date | undefined;
    // the elapsed time of the timer as the server told it, and when the device read it
    timerElapsedSeconds: number;
    timerElapsedReadDateTime: Date;
    timerMinuteRate: number | undefined;
    timerDiscounts: IBillItemTimerDiscount[];
    quantity: number;
    paidQuantity: number;
    unpaidQuantity: number;
    paidAmount: number;
    unitPrice: number;
    unitPriceWithOptions: number;
    isZeroPriceItem: boolean;
    sortOrder: number;
    options: Array<IBillItemOptionGroup>;
    unitPriceAfterDiscount: number;
    unitPriceAfterDiscountWithOptions: number;
    lastDiscountType: DiscountType | undefined;
    hasDiscount: boolean;
    hasProcessedItemLevelDiscount: boolean;

    constructor(lineItem: IBillItem) {
        this.id = lineItem.id;
        this.orderId = lineItem.orderId;
        this.orderItemId = lineItem.orderItemId;
        this.articleId = lineItem.articleId;
        this.name = lineItem.name;
        this.internalName = lineItem.internalName;
        this.comment = lineItem.comment;
        this.isPricePerMinute = lineItem.isPricePerMinute;
        this.timerStartedDateTime = lineItem.timerStartedDateTime;
        this.timerStoppedDateTime = lineItem.timerStoppedDateTime;
        this.timerElapsedSeconds = lineItem.timerElapsedSeconds;
        this.timerElapsedReadDateTime = lineItem.timerElapsedReadDateTime;
        this.timerMinuteRate = lineItem.timerMinuteRate;
        this.timerDiscounts = lineItem.timerDiscounts;
        this.quantity = lineItem.quantity;
        this.paidQuantity = lineItem.paidQuantity;
        this.unpaidQuantity = lineItem.unpaidQuantity;
        this.paidAmount = lineItem.paidAmount;
        this.unitPrice = lineItem.unitPrice;
        this.unitPriceWithOptions = lineItem.unitPriceWithOptions;
        this.isZeroPriceItem = lineItem.isZeroPriceItem;
        this.sortOrder = lineItem.sortOrder;
        this.options = lineItem.options;
        this.unitPriceAfterDiscount = lineItem.unitPriceAfterDiscount;
        this.unitPriceAfterDiscountWithOptions = lineItem.unitPriceAfterDiscountWithOptions;
        this.lastDiscountType = lineItem.lastDiscountType;
        this.hasDiscount = lineItem.hasDiscount;
        this.hasProcessedItemLevelDiscount = lineItem.hasProcessedItemLevelDiscount;
    }
}

export type OpenBillSelectedEventData = {
    locationId: string;
    tableId: string;
    roomId: string;
    tableNumber: number;
    selectedOpenBill: IBaseOpenBill;
};

export type OpenBillRoomSelectedEventData = {
    locationId: string;
    roomId: string;
};

export type OpenBillTableData = {
    name: string;
    locationId: string;
    roomId: string;
    roomName: string;
    tableId: string;
    tableNumber: number;
    tableName: string;
    operatorName: string;
    occupancy: string;
    since: string;
    open: string;
    isDisabled: boolean;
    bill: IBaseOpenBill;
};

export interface IBillLastOperator {
    identityId: string;
    name: string;
}